/**
 * Dirty tracking for large forms that own their state internally.
 *
 * The baseline is the form snapshot taken at the user's first interaction (capture phase, so it
 * runs before the change is applied) and again after each successful save. This avoids counting
 * the form's own load/normalise effects as edits. Spread `captureProps` on an element that wraps
 * the form fields.
 */

import { useCallback, useMemo, useRef } from 'react';

export interface DirtySnapshotCaptureProps {
  onChangeCapture: () => void;
  onInputCapture: () => void;
  onClickCapture: () => void;
  onKeyDownCapture: () => void;
}

export interface UseDirtySnapshotResult {
  /** True when the current snapshot differs from the baseline */
  isDirty: () => boolean;
  /** Set the baseline to `snapshot` (or the current snapshot), e.g. after a successful save */
  markClean: (snapshot?: string) => void;
  /** Forget the baseline (e.g. when a different record is loaded) */
  reset: () => void;
  /** Current snapshot (string) */
  takeSnapshot: () => string;
  captureProps: DirtySnapshotCaptureProps;
}

export function useDirtySnapshot(getSnapshot: () => string | null | undefined): UseDirtySnapshotResult {
  const getRef = useRef(getSnapshot);
  getRef.current = getSnapshot;
  const baselineRef = useRef<string | null>(null);

  const takeSnapshot = useCallback(() => getRef.current() ?? '', []);

  const captureBaseline = useCallback(() => {
    if (baselineRef.current === null) {
      baselineRef.current = takeSnapshot();
    }
  }, [takeSnapshot]);

  const isDirty = useCallback(() => {
    if (baselineRef.current === null) return false;
    return takeSnapshot() !== baselineRef.current;
  }, [takeSnapshot]);

  const markClean = useCallback((snapshot?: string) => {
    baselineRef.current = snapshot ?? takeSnapshot();
  }, [takeSnapshot]);

  const reset = useCallback(() => {
    baselineRef.current = null;
  }, []);

  const captureProps = useMemo<DirtySnapshotCaptureProps>(() => ({
    onChangeCapture: captureBaseline,
    onInputCapture: captureBaseline,
    onClickCapture: captureBaseline,
    onKeyDownCapture: captureBaseline,
  }), [captureBaseline]);

  return { isDirty, markClean, reset, takeSnapshot, captureProps };
}

export default useDirtySnapshot;
